import { Modal } from "@shopify/polaris"
import { capitalize, cloneDeep } from "lodash-es"
import { useEffect, useMemo, useState } from "react"

import { PageInfo } from "@/api/common/type"

import emitter from "../emitter"
import { apis } from "./apis"
import ResourcePickerInner from "./ResourcePickerInner"
import { QueryFilterProps } from "./types"

let timer: NodeJS.Timeout | null = null
export const ResourcePicker = () => {
  const [config, setConfig] = useState<null | QueryFilterProps>()

  const handleConfirm = () => {
    const selectedItems = selectedItemsNew.map((product) => {
      const item = itemsMap[product.id] || {}
      return {
        id: product.id,
        ...item,
        variants:
          product.variants?.map((variant) => {
            return {
              id: variant,
              ...variantMap[variant],
            }
          }) ?? [],
      }
    })
    config?.onConfirm?.(selectedItems) // 调用传入的确认回调，返回选中的内容
    setConfig(null)
  }

  const handleClose = () => {
    config?.onClose?.() // 调用传入的关闭回调
    setConfig(null)
  }

  const [isLoading, setIsLoading] = useState(false)
  const [items, setItems] = useState<any>([])
  const [pageInfo, setPageInfo] = useState<PageInfo>({
    endCursor: "",
    hasNextPage: false,
    hasPreviousPage: false,
    startCursor: "",
  })
  const [selectedItemsNew, setSelectedItemsNew] = useState<
    {
      id: string
      variants: string[]
    }[]
  >([])
  const [queryValue, setQueryValue] = useState("")

  const { itemsMap, variantMap } = useMemo(() => {
    const itemsMap = {}
    const variantMap = {}
    items.forEach((item) => {
      itemsMap[item.id] = item
      item.variants?.nodes?.forEach((variant) => {
        variantMap[variant.id] = variant
      })
    })
    return { itemsMap, variantMap }
  }, [items])

  const { selectedItemsMap, selectedVariantMap, count } = useMemo(() => {
    const selectedItemsMap: Record<string, boolean | "indeterminate"> = {}
    const selectedVariantMap: Record<string, true> = {}
    let count = 0

    selectedItemsNew.forEach((item) => {
      item.variants?.forEach((variant) => {
        selectedVariantMap[variant] = true
      })
      // 如果有variants.length，判断是否所有variants都被选中
      let checked: boolean | "indeterminate" = true
      const variants = itemsMap[item.id]?.variants?.nodes
      const hasVariants =
        variants?.length > 0 && config?.filter?.variants !== false
      // 如果有变体
      if (hasVariants) {
        if (variants.every((variant) => selectedVariantMap[variant.id])) {
          checked = true
        } else if (variants.some((variant) => selectedVariantMap[variant.id])) {
          checked = "indeterminate"
        } else {
          checked = false
        }
      } else {
        // 如果没有变体，判断是否被选中
        checked = true
      }
      selectedItemsMap[item.id] = checked
      count += 1
    })
    return { selectedItemsMap, selectedVariantMap, count }
  }, [selectedItemsNew, itemsMap, config?.filter?.variants])

  const handleScrolledToBottom = () => {
    if (isLoading || !pageInfo.hasNextPage) return
    fetchData({
      after: pageInfo.endCursor,
    })
  }

  function handleSelectedItemWithoutVariant(info: {
    id: string
    checked: boolean
    variants: string[]
  }) {
    if (info.checked) {
      selectedItemsNew.push({ id: info.id, variants: [] })
    } else {
      const index = selectedItemsNew.findIndex((item) => item.id === info.id)
      if (index !== -1) {
        selectedItemsNew.splice(index, 1)
      }
    }
    setSelectedItemsNew(cloneDeep(selectedItemsNew))
  }

  function handleSelectedItemWithVariant(info: {
    id: string
    checked: boolean
    variants: string[]
  }) {
    const index = selectedItemsNew.findIndex((item) => item.id === info.id)

    if (info.checked) {
      if (index !== -1) {
        selectedItemsNew[index].variants = info.variants
      } else {
        selectedItemsNew.push({ id: info.id, variants: info.variants })
      }
    } else {
      if (index !== -1) {
        selectedItemsNew.splice(index, 1)
      }
    }
    setSelectedItemsNew(cloneDeep(selectedItemsNew))
  }

  function handleSelectedItem(info: {
    id: string
    checked: boolean
    variants: string[]
  }) {
    const hasVariants =
      itemsMap[info.id]?.variants?.nodes?.length > 0 &&
      config?.filter?.variants !== false
    if (hasVariants) {
      handleSelectedItemWithVariant(info)
    } else {
      handleSelectedItemWithoutVariant(info)
    }
  }

  function handleSelectedVariant(info: {
    id: string
    pid: string
    checked: boolean
  }) {
    const index = selectedItemsNew.findIndex((item) => item.id === info.pid)
    if (index !== -1) {
      if (info.checked) {
        selectedItemsNew[index].variants = [
          ...selectedItemsNew[index].variants,
          info.id,
        ]
      } else {
        const newVariants = selectedItemsNew[index].variants.filter(
          (v) => v !== info.id,
        )
        if (newVariants.length === 0) {
          selectedItemsNew.splice(index, 1)
        } else {
          selectedItemsNew[index].variants = newVariants
        }
      }
    } else {
      selectedItemsNew.push({ id: info.pid, variants: [info.id] })
    }
    setSelectedItemsNew(cloneDeep(selectedItemsNew))
  }

  useEffect(() => {
    const handleShowModal = (config: any) => {
      setConfig(config) // 设置过滤器参数
      setSelectedItemsNew(config.selectionIds || [])
      fetchData({ after: "", keyword: "", type: config.type })
    }
    emitter.on("common:resourcePicker:show", handleShowModal)
    return () => {
      emitter.off("common:resourcePicker:show", handleShowModal) // 清理事件监听器
    }
  }, [])

  async function fetchData(params: {
    after?: string
    keyword?: string
    type?: string
  }) {
    const api = apis[params.type || config?.type || "product"]
    if (!api) return
    const realParamns = {
      before: "",
      paginate: 50,
      keyword: queryValue,
      after: pageInfo.endCursor,
      ...params,
    }
    setIsLoading(true)
    const res = await api(realParamns)
    if (!res) {
      setIsLoading(false)
      return
    }
    const { nodes, pageInfo: newPageInfo } = res
    setPageInfo(newPageInfo)

    if (realParamns.after) {
      setItems([...items, ...nodes])
    } else {
      setItems(nodes)
    }
    setIsLoading(false)
  }

  function handleQueryChange(value: string) {
    setQueryValue(value)
    timer && clearTimeout(timer)
    timer = setTimeout(() => {
      fetchData({
        keyword: value,
        after: "",
      })
    }, 300)
  }
  return (
    <div className="ResourcePicker">
      <Modal
        open={!!config}
        onClose={handleClose}
        title={`${capitalize(config?.action || "Add")} ${capitalize(config?.type)}`}
        primaryAction={{
          content: "Select",
          onAction: handleConfirm,
          disabled: count === 0,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: handleClose,
          },
        ]}
        limitHeight
        footer={`${count} / ${items.length} ${config?.type}s selected`}
      >
        {config && (
          <ResourcePickerInner
            type={(config.type as any) || "product"}
            handleScrolledToBottom={handleScrolledToBottom}
            queryValue={queryValue}
            handleQueryChange={handleQueryChange}
            isLoading={isLoading}
            items={items}
            selectedItemsMap={selectedItemsMap}
            selectedVariantMap={selectedVariantMap}
            handleSelectedItem={handleSelectedItem}
            handleSelectedVariant={handleSelectedVariant}
            multiple={config.multiple || false}
            filter={config.filter || {}}
          />
        )}
      </Modal>
    </div>
  )
}
